// Hypeddit HTML parser for campaign and smart link exports
import * as cheerio from 'cheerio';
import type { 
  ParsedHypedditData, 
  HypedditCampaignMetric, 
  HypedditSmartlinkMetric,
  FileValidationResult 
} from '../../shared/types/analytics';
import { deduplicationService } from './deduplication';

export class HypedditParserService {
  /**
   * Validate HTML file structure
   */
  validateHypedditHTML(htmlContent: string): FileValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    try {
      // Parse HTML with cheerio
      const $ = cheerio.load(htmlContent);
      
      if (!htmlContent.trim()) {
        errors.push('HTML file appears to be empty');
        return {
          valid: false,
          errors,
          warnings,
          file_info: { size: htmlContent.length, type: 'html' }
        };
      }

      // Check for Hypeddit-specific content indicators
      let isHypedditContent = false;
      const hypedditIndicators = [
        'hypeddit',
        'campaign performance',
        'smart link',
        'smartlink',
        'conversion',
        'ad metrics'
      ];

      const textContent = $('*').text().toLowerCase();
      for (const indicator of hypedditIndicators) {
        if (textContent.includes(indicator)) {
          isHypedditContent = true;
          break;
        }
      }

      if (!isHypedditContent) {
        warnings.push('This HTML file may not be from Hypeddit - unable to find expected content markers');
      }

      // Look for data tables or structured content
      const tables = $('table').length;
      const divs = $('div').length;
      const dataContainers = $('.stats, .metrics, .performance, .campaign, .data-table, .summary').length;

      if (tables === 0 && dataContainers === 0 && divs < 5) {
        errors.push('No structured data found in HTML - expected tables or data containers');
      }

      // Check for potential data indicators
      const hasNumbers = /\d+/.test(textContent);

      if (!hasNumbers) {
        warnings.push('No numeric data detected in HTML');
      }

      let estimated_records = 0;
      
      // Estimate record count from tables and data sections
      $('table tr, .campaign-item, .smartlink-item, .data-row').each(() => {
        estimated_records++;
      });

      // Fallback estimation from text patterns
      if (estimated_records === 0) {
        const lines = textContent.split('\n').filter((line: string) => 
          line.trim() && /\d/.test(line)
        );
        estimated_records = Math.floor(lines.length / 3); // Rough estimation
      }

      return {
        valid: errors.length === 0,
        errors,
        warnings,
        file_info: {
          size: htmlContent.length,
          type: 'html',
          estimated_records
        }
      };
    } catch (error) {
      return {
        valid: false,
        errors: [`HTML parsing error: ${error}`],
        warnings,
        file_info: { size: htmlContent.length, type: 'html' }
      };
    }
  }

  /**
   * Extract date range from HTML content
   */
  extractDateRange(htmlContent: string): { start: string; end: string } {
    const $ = cheerio.load(htmlContent);
    const textContent = $('*').text();
    
    // Look for date patterns in the HTML
    const datePatterns = [
      /(\d{4}[-\/]\d{2}[-\/]\d{2})/g,
      /(\d{2}[-\/]\d{2}[-\/]\d{4})/g,
      /(\w+\s+\d{1,2},?\s+\d{4})/g // "January 15, 2025"
    ];

    const foundDates: Date[] = [];

    for (const pattern of datePatterns) {
      const matches = textContent.match(pattern);
      if (matches) {
        for (const match of matches) {
          const date = new Date(match);
          if (!isNaN(date.getTime())) {
            foundDates.push(date);
          }
        }
      }
    }

    if (foundDates.length >= 2) {
      foundDates.sort((a, b) => a.getTime() - b.getTime());
      return {
        start: foundDates[0].toISOString().split('T')[0],
        end: foundDates[foundDates.length - 1].toISOString().split('T')[0]
      };
    }

    // Fallback to recent period
    const today = new Date();
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    return {
      start: weekAgo.toISOString().split('T')[0],
      end: today.toISOString().split('T')[0]
    };
  }

  /**
   * Extract campaign metrics from HTML
   */
  extractCampaignMetrics($: ReturnType<typeof cheerio.load>, artist_id?: string): Array<Omit<HypedditCampaignMetric, 'id' | 'upload_id' | 'created_at'>> {
    const campaigns: Array<Omit<HypedditCampaignMetric, 'id' | 'upload_id' | 'created_at'>> = [];
    const today = new Date().toISOString().split('T')[0];

    // Strategy 1: Look for campaign tables
    const tables = $('table').toArray();
    for (const table of tables) {
      const $table = $(table);
      const headers = $table.find('thead tr th, tr:first-child td, tr:first-child th')
        .map((_, el) => $(el).text().toLowerCase().trim()).get();

      // Check if this looks like a campaign table
      if (headers.some(h => h.includes('campaign') || h.includes('ad'))) {
        const rows = $table.find('tbody tr, tr').slice(1).toArray();
        for (const row of rows) {
          const $row = $(row);
          const cells = $row.find('td, th').map((_, cell) => $(cell).text().trim()).get();
          
          if (cells.length >= 3) {
          const campaign = this.extractCampaignFromRow(cells, headers, artist_id, today);
            if (campaign) campaigns.push(campaign);
          }
        }
      }
    }

    // Strategy 2: Look for campaign sections/divs
    $('.campaign, .ad-campaign, .campaign-item, .campaign-summary').each((_, element) => {
      const $element = $(element);
      const campaign = this.extractCampaignFromElement($element, artist_id, today);
      if (campaign) campaigns.push(campaign);
    });

    // Strategy 3: Pattern-based extraction from text
    if (campaigns.length === 0) {
      campaigns.push(...this.extractCampaignsFromText($('*').text(), artist_id, today));
    }

    return campaigns;
  }

  private extractCampaignFromRow(
    cells: string[], 
    headers: string[], 
    artist_id?: string, 
    recorded_at?: string
  ): Omit<HypedditCampaignMetric, 'id' | 'upload_id' | 'created_at'> | null {
    let campaign_name = '';
    let impressions = 0;
    let clicks = 0;
    let cost = 0;
    let reach = 0;

    // Map cells to data based on headers
    for (let i = 0; i < Math.min(cells.length, headers.length); i++) {
      const header = headers[i];
      const value = cells[i];

      if (header.includes('name') || header.includes('campaign')) {
        campaign_name = value;
      } else if (header.includes('impression')) {
        impressions = this.parseNumber(value);
      } else if (header.includes('click')) {
        clicks = this.parseNumber(value);
      } else if (header.includes('cost') || header.includes('spend')) {
        cost = this.parseNumber(value);
      } else if (header.includes('reach')) {
        reach = this.parseNumber(value);
      }
    }

    if (!campaign_name) return null;

    const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
    
    const campaignData = {
      artist_id,
      campaign_name,
      campaign_type: 'promotion',
      status: 'active' as const,
      impressions,
      clicks,
      reach,
      ctr: Math.round(ctr * 100) / 100,
      cost,
      currency: 'USD',
      downloads: 0, // Will be updated if found
      conversion_rate: 0,
      recorded_at: recorded_at || new Date().toISOString().split('T')[0],
      data_hash: ''
    };

    campaignData.data_hash = deduplicationService.generateDataHash(campaignData, 'hypeddit', 'campaign');
    return campaignData;
  }

  private extractCampaignFromElement(
    $element: any, 
    artist_id?: string, 
    recorded_at?: string
  ): Omit<HypedditCampaignMetric, 'id' | 'upload_id' | 'created_at'> | null {
    const text = $element.text();
    const campaign_name = $element.find('.name, .title, .campaign-name').first().text() || 
                         text.split('\n')[0].trim();

    if (!campaign_name) return null;

    const impressions = this.extractNumberFromText(text, ['impression', 'view']);
    const clicks = this.extractNumberFromText(text, ['click', 'tap']);
    const cost = this.extractNumberFromText(text, ['cost', 'spend', '$']);
    const reach = this.extractNumberFromText(text, ['reach', 'audience']);

    const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;

    const campaignData = {
      artist_id,
      campaign_name,
      campaign_type: 'promotion',
      status: 'active' as const,
      impressions,
      clicks,
      reach,
      ctr: Math.round(ctr * 100) / 100,
      cost,
      currency: 'USD',
      downloads: 0,
      conversion_rate: 0,
      recorded_at: recorded_at || new Date().toISOString().split('T')[0],
      data_hash: ''
    };

    campaignData.data_hash = deduplicationService.generateDataHash(campaignData, 'hypeddit', 'campaign');
    return campaignData;
  }

  private extractCampaignsFromText(
    text: string, 
    artist_id?: string, 
    recorded_at?: string
  ): Array<Omit<HypedditCampaignMetric, 'id' | 'upload_id' | 'created_at'>> {
    const campaigns: Array<Omit<HypedditCampaignMetric, 'id' | 'upload_id' | 'created_at'>> = [];

    // Look for campaign patterns in text
    const lines = text.split('\n').filter(line => line.trim());
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Look for lines that might be campaign names
      if (line.length > 5 && line.length < 100 && 
          !line.match(/^\d+$/) && 
          !line.includes('%') &&
          (line.toLowerCase().includes('campaign') || 
           line.toLowerCase().includes('ad') ||
           i === 0)) { // First line might be campaign name
        
        // Look ahead for metrics
        const metrics = this.extractMetricsFromFollowingLines(lines.slice(i + 1, i + 10));
        
        if (metrics.impressions > 0 || metrics.clicks > 0 || metrics.cost > 0) {
          const ctr = metrics.impressions > 0 ? (metrics.clicks / metrics.impressions) * 100 : 0;
          
          const campaignData = {
            artist_id,
            campaign_name: line.trim(),
            campaign_type: 'promotion',
            status: 'summary' as const,
            impressions: metrics.impressions,
            clicks: metrics.clicks,
            reach: metrics.reach,
            ctr: Math.round(ctr * 100) / 100,
            cost: metrics.cost,
            currency: 'USD',
            downloads: metrics.downloads,
            conversion_rate: 0,
            recorded_at: recorded_at || new Date().toISOString().split('T')[0],
            data_hash: ''
          };

          campaignData.data_hash = deduplicationService.generateDataHash(campaignData, 'hypeddit', 'campaign');
          campaigns.push(campaignData);
        }
      }
    }

    return campaigns;
  }

  /**
   * Extract smart link metrics from HTML
   */
  extractSmartlinkMetrics($: ReturnType<typeof cheerio.load>, artist_id?: string): Array<Omit<HypedditSmartlinkMetric, 'id' | 'upload_id' | 'created_at'>> {
    const smartlinks: Array<Omit<HypedditSmartlinkMetric, 'id' | 'upload_id' | 'created_at'>> = [];
    const today = new Date().toISOString().split('T')[0];

    // Strategy 1: Look for smart link tables
    $('table').each((_, table) => {
      const $table = $(table);
      const headers = $table.find('thead tr th, tr:first-child td, tr:first-child th')
        .map((_, el) => $(el).text().toLowerCase().trim()).get();

      if (headers.some(h => h.includes('link') || h.includes('smart'))) {
        $table.find('tbody tr, tr').slice(1).each((_, row) => {
          const $row = $(row);
          const cells = $row.find('td, th').map((_, cell) => $(cell).text().trim()).get();
          
          if (cells.length >= 3) {
            const smartlink = this.extractSmartlinkFromRow(cells, headers, artist_id, today);
            if (smartlink) smartlinks.push(smartlink);
          }
        });
      }
    });

    // Strategy 2: Look for smart link sections
    $('.smartlink, .smart-link, .link-item').each((_, element) => {
      const $element = $(element);
      const smartlink = this.extractSmartlinkFromElement($element, artist_id, today);
      if (smartlink) smartlinks.push(smartlink);
    });

    return smartlinks;
  }

  private extractSmartlinkFromRow(
    cells: string[], 
    headers: string[], 
    artist_id?: string, 
    recorded_at?: string
  ): Omit<HypedditSmartlinkMetric, 'id' | 'upload_id' | 'created_at'> | null {
    let link_title = '';
    let clicks = 0;
    let conversions = 0;
    let link_url = '';

    for (let i = 0; i < Math.min(cells.length, headers.length); i++) {
      const header = headers[i];
      const value = cells[i];

      if (header.includes('title') || header.includes('name') || header.includes('link')) {
        if (value.startsWith('http')) {
          link_url = value;
        } else {
          link_title = value;
        }
      } else if (header.includes('click')) {
        clicks = this.parseNumber(value);
      } else if (header.includes('conversion') || header.includes('download')) {
        conversions = this.parseNumber(value);
      }
    }

    if (!link_title) return null;

    const conversion_rate = clicks > 0 ? (conversions / clicks) * 100 : 0;

    const smartlinkData = {
      artist_id,
      link_title,
      link_url: link_url || undefined,
      short_url: undefined,
      song_title: undefined,
      clicks,
      conversions,
      conversion_rate: Math.round(conversion_rate * 100) / 100,
      top_platform: undefined,
      platform_breakdown: undefined,
      recorded_at: recorded_at || new Date().toISOString().split('T')[0],
      data_hash: ''
    };

    smartlinkData.data_hash = deduplicationService.generateDataHash(smartlinkData, 'hypeddit', 'smartlink');
    return smartlinkData;
  }

  private extractSmartlinkFromElement(
    $element: any, 
    artist_id?: string, 
    recorded_at?: string
  ): Omit<HypedditSmartlinkMetric, 'id' | 'upload_id' | 'created_at'> | null {
    const text = $element.text();
    const link_title = $element.find('.title, .name, .link-title').first().text() || 
                      text.split('\n')[0].trim();

    if (!link_title) return null;

    const clicks = this.extractNumberFromText(text, ['click', 'tap', 'visit']);
    const conversions = this.extractNumberFromText(text, ['conversion', 'download', 'stream']);
    const conversion_rate = clicks > 0 ? (conversions / clicks) * 100 : 0;

    const smartlinkData = {
      artist_id,
      link_title,
      link_url: undefined,
      short_url: undefined,
      song_title: undefined,
      clicks,
      conversions,
      conversion_rate: Math.round(conversion_rate * 100) / 100,
      top_platform: undefined,
      platform_breakdown: undefined,
      recorded_at: recorded_at || new Date().toISOString().split('T')[0],
      data_hash: ''
    };

    smartlinkData.data_hash = deduplicationService.generateDataHash(smartlinkData, 'hypeddit', 'smartlink');
    return smartlinkData;
  }

  // Helper methods
  private parseNumber(value: string): number {
    if (!value) return 0;
    
    // Remove non-numeric characters except decimal point and commas
    const cleaned = value.replace(/[^\d.,]/g, '');
    const number = parseFloat(cleaned.replace(/,/g, ''));
    
    return isNaN(number) ? 0 : number;
  }

  private extractNumberFromText(text: string, keywords: string[]): number {
    for (const keyword of keywords) {
      const pattern = new RegExp(`${keyword}[\\s:]*([\\d,]+)`, 'i');
      const match = text.match(pattern);
      if (match) {
        return this.parseNumber(match[1]);
      }
    }
    return 0;
  }

  private extractMetricsFromFollowingLines(lines: string[]): {
    impressions: number;
    clicks: number;
    cost: number;
    reach: number;
    downloads: number;
  } {
    let impressions = 0;
    let clicks = 0;
    let cost = 0;
    let reach = 0;
    let downloads = 0;

    for (const line of lines) {
      const lower = line.toLowerCase();
      
      if (lower.includes('impression') || lower.includes('view')) {
        impressions = Math.max(impressions, this.extractNumberFromText(line, ['']));
      }
      if (lower.includes('click') || lower.includes('tap')) {
        clicks = Math.max(clicks, this.extractNumberFromText(line, ['']));
      }
      if (lower.includes('cost') || lower.includes('spend') || line.includes('$')) {
        cost = Math.max(cost, this.extractNumberFromText(line, ['']));
      }
      if (lower.includes('reach') || lower.includes('audience')) {
        reach = Math.max(reach, this.extractNumberFromText(line, ['']));
      }
      if (lower.includes('download') || lower.includes('conversion')) {
        downloads = Math.max(downloads, this.extractNumberFromText(line, ['']));
      }
    }

    return { impressions, clicks, cost, reach, downloads };
  }

  /**
   * Main parsing method
   */
  async parseHypedditHTML(
    htmlContent: string,
    _filename: string,
    artist_id?: string
  ): Promise<ParsedHypedditData> {
    try {
      const validation = this.validateHypedditHTML(htmlContent);
      
      if (!validation.valid) {
        throw new Error(`HTML validation failed: ${validation.errors.join(', ')}`);
      }

      const $ = cheerio.load(htmlContent);
      const date_range = this.extractDateRange(htmlContent);

      const campaigns = this.extractCampaignMetrics($, artist_id);
      const smartlinks = this.extractSmartlinkMetrics($, artist_id);

      return {
        campaigns,
        smartlinks,
        date_range
      };
    } catch (error) {
      throw new Error(`Failed to parse Hypeddit HTML: ${error}`);
    }
  }
}

export const hypedditParserService = new HypedditParserService();
